import { useState, useCallback } from 'react'
import type { JSX } from 'react'

import {
  Button,
  Dialog,
  Flex,
  Form,
  Input,
  Label,
  Notice,
  Skeleton,
  Switch,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { validateInvitationName } from '~publish/helpers/validateInvitationName'

import { useAssignableChannels, useInviteTeamMembers } from '../../hooks'
import type { AccessLevel, ProductAccessLevels, ProductKey } from '../../types'

import { ChannelPermissionsSection } from '../ChannelPermissions'

import styles from './InviteMemberDialog.module.css'

const DEFAULT_LEVELS: ProductAccessLevels = {
  publish: 'fullPosting',
  comments: 'fullPosting',
}

function validateEmail(value: string): boolean {
  if (value.trim() === '') return true // Don't show error for empty field
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

interface InviteMemberDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onClose: () => void
}

export const InviteMemberDialog = ({
  isOpen,
  onOpenChange,
  onClose,
}: InviteMemberDialogProps): JSX.Element => {
  const organizationId = useOrganizationId()

  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<'admin' | 'member'>('member')
  // Channel access: a global pair applied to all channels (collapsed) plus
  // per-channel overrides (expanded). `isCustomized` toggles between them.
  const [globalLevels, setGlobalLevels] =
    useState<ProductAccessLevels>(DEFAULT_LEVELS)
  const [overrides, setOverrides] = useState<Map<string, ProductAccessLevels>>(
    new Map(),
  )
  const [isCustomized, setIsCustomized] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [nameError, setNameError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const { assignableChannels, loading: loadingChannels } =
    useAssignableChannels({
      organizationId: organizationId ?? '',
    })

  const { inviteTeamMember, loading: inviting } = useInviteTeamMembers()

  const handleEmailBlur = useCallback(() => {
    if (email.trim() !== '' && !validateEmail(email)) {
      setEmailError('Please enter a valid email address')
    } else {
      setEmailError('')
    }
  }, [email])

  const handleNameBlur = useCallback(() => {
    setNameError(validateInvitationName(name) ?? '')
  }, [name])

  const resetForm = useCallback(() => {
    setEmail('')
    setName('')
    setRole('member')
    setGlobalLevels(DEFAULT_LEVELS)
    setOverrides(new Map())
    setIsCustomized(false)
    setEmailError('')
    setNameError('')
    setSubmitError('')
    setHasSubmitted(false)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [resetForm, onClose])

  // Funnel every dismissal (Cancel, Escape, backdrop) through `handleClose` so
  // the form always resets. Passing `onOpenChange` straight to Dialog would
  // skip the reset on Escape/backdrop.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        onOpenChange(true)
      } else {
        handleClose()
      }
    },
    [onOpenChange, handleClose],
  )

  // Collapsed state: update one product's level for all channels.
  const handleGlobalLevelChange = useCallback(
    (product: ProductKey, level: AccessLevel) => {
      setGlobalLevels((prev) => ({ ...prev, [product]: level }))
    },
    [],
  )

  // Expanded state: update one product's level on a single channel.
  const handleOverrideChange = useCallback(
    (channelId: string, product: ProductKey, level: AccessLevel) => {
      setOverrides((prev) => {
        const next = new Map(prev)
        const current = next.get(channelId) ?? globalLevels
        next.set(channelId, { ...current, [product]: level })
        return next
      })
    },
    [globalLevels],
  )

  // Customize: seed every channel's overrides from the global pair, then
  // switch to the per-channel table.
  const handleCustomize = useCallback(() => {
    if (!assignableChannels) return
    setOverrides(
      new Map(
        assignableChannels.map(({ channel }) => [
          channel.id,
          { ...globalLevels },
        ]),
      ),
    )
    setIsCustomized(true)
  }, [assignableChannels, globalLevels])

  // Reset: drop overrides and collapse back. The global pair is preserved.
  const handleResetCustomization = useCallback(() => {
    setOverrides(new Map())
    setIsCustomized(false)
  }, [])

  const handleSubmit = async (): Promise<void> => {
    if (!organizationId) return

    setSubmitError('')
    setHasSubmitted(true)

    if (email.trim() === '' || name.trim() === '') {
      return
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    // The server rejects the same names (INC-442). Checking here turns a
    // failed mutation into inline feedback on the field that caused it.
    const nameProblem = validateInvitationName(name)
    if (nameProblem) {
      setNameError(nameProblem)
      return
    }

    // Send every channel explicitly with both products — the mutation requires
    // both keys; the backend drops `none` grants.
    const assignments = (assignableChannels ?? []).map(({ channel }) => ({
      channelId: channel.id,
      productAccessLevels: {
        ...(isCustomized
          ? (overrides.get(channel.id) ?? globalLevels)
          : globalLevels),
      },
    }))
    const result = await inviteTeamMember({
      organizationId,
      email,
      name,
      role,
      channelAssignments: assignments,
    })

    if (result.success) {
      toast.success('Team member invited successfully')
      handleClose()
    } else {
      setSubmitError(result.error ?? 'Failed to invite team member')
    }
  }

  const showEmailRequired = hasSubmitted && email.trim() === ''
  const showNameRequired = hasSubmitted && name.trim() === ''

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content size="medium" className={styles.dialogContent}>
        <Dialog.Header>
          <Dialog.Title>Invite Team Member</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <div className={styles.form}>
            {submitError && <Notice variant="error">{submitError}</Notice>}
            <Form.Field name="invite-email">
              <Form.Label>Email</Form.Label>
              <Form.Control>
                <Input
                  type="email"
                  placeholder="email@example.com"
                  value={email}
                  onChange={(e): void => {
                    setEmail(e.target.value)
                    if (emailError) setEmailError('')
                    if (submitError) setSubmitError('')
                  }}
                  onBlur={handleEmailBlur}
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                  aria-invalid={showEmailRequired || emailError !== ''}
                  aria-describedby={
                    showEmailRequired || emailError
                      ? 'invite-email-error'
                      : undefined
                  }
                />
              </Form.Control>
              {showEmailRequired && (
                <Text size="sm" color="critical" id="invite-email-error">
                  Email is required
                </Text>
              )}
              {!showEmailRequired && emailError && (
                <Text id="invite-email-error" color="critical" size="sm">
                  {emailError}
                </Text>
              )}
            </Form.Field>

            <Form.Field name="invite-name">
              <Form.Label>Name</Form.Label>
              <Form.Control>
                <Input
                  type="text"
                  placeholder="Team member name"
                  value={name}
                  onChange={(e): void => {
                    setName(e.target.value)
                    if (nameError) setNameError('')
                    if (submitError) setSubmitError('')
                  }}
                  onBlur={handleNameBlur}
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                  aria-invalid={showNameRequired || nameError !== ''}
                  aria-describedby={
                    showNameRequired || nameError
                      ? 'invite-name-error'
                      : undefined
                  }
                />
              </Form.Control>
              {showNameRequired && (
                <Text size="sm" color="critical" id="invite-name-error">
                  Name is required
                </Text>
              )}
              {!showNameRequired && nameError && (
                <Text id="invite-name-error" color="critical" size="sm">
                  {nameError}
                </Text>
              )}
            </Form.Field>

            {/* Same structure and default (medium) switch as the edit-member
                dialog, so the toggle stays comfortably tappable and the
                description never crowds the label row (CORE-6243). */}
            <Flex align="center" justify="between" gap="md">
              <Flex direction="column" gap="2xs">
                <Label htmlFor="admin-toggle">Admin</Label>
                <Text color="subtle" size="sm">
                  Can manage team members and team settings
                </Text>
              </Flex>
              <Switch
                id="admin-toggle"
                checked={role === 'admin'}
                onCheckedChange={(checked): void =>
                  setRole(checked ? 'admin' : 'member')
                }
              />
            </Flex>

            {loadingChannels ? (
              <Flex direction="column" gap="xs" fullWidth>
                <Skeleton height="48px" width="100%" />
                <Skeleton height="48px" width="100%" />
              </Flex>
            ) : assignableChannels?.length ? (
              // The expanded per-channel table owns its own channel search
              // (shown only when customized), so the section gets the full list.
              <ChannelPermissionsSection
                channels={assignableChannels}
                globalLevels={globalLevels}
                overrides={overrides}
                isCustomized={isCustomized}
                onGlobalChange={handleGlobalLevelChange}
                onOverrideChange={handleOverrideChange}
                onCustomize={handleCustomize}
                onReset={handleResetCustomization}
              />
            ) : (
              <Text color="subtle">No channels available</Text>
            )}
          </div>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="tertiary" size="medium" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSubmit}
            disabled={inviting}
            loading={inviting}
          >
            Send Invite
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
